import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
  Alert,
  Paper,
  InputLabel,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { WifiOff, Wifi } from "@mui/icons-material";
import { StyledTextField } from "../../../../../customComponents/styled/styledInputs";
import { useFormik } from "formik";
import * as yup from "yup";
import { FilledButton } from "../../../../../customComponents/styled/styledButtons";
import {
  Check,
  TaskAlt,
  ArrowRightAlt,
  Close,
  Info,
} from "@mui/icons-material";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useCreateQuizMutation,
  useGetClassroomStudentsQuery,
  useGetClassroomAssessmentsQuery,
  useGetTopicsBySubjectAndLevelQuery,
} from "../../../../../../app/quizApiSlice";
import {
  useGetTeacherSubjectsQuery,
  useGetClassroomSubjectsQuery,
} from "../../../../../../app/questionApiSlice";
import { useGetClassesPaginatedQuery } from "../../../../classroom/classApiSlice";
import { selectCurrentUser } from "../../../../auth/authSlice";
import CustomTipTapEditor from "../../../../../../customComponents/CustomTipTapEditor";
import { localToUTC } from "../../../../../../helpers/dateUtils";

// Validation Schema
const validationSchema = yup.object({
  classroom_id: yup.string().required("Class is required"),
  subject: yup.string().required("Subject is required"),
  type: yup.string().required("Quiz type is required"),
  selectedTopics: yup.array().min(1, "Select at least one topic"),
  is_offline: yup.boolean(),
  due_at: yup.string().when("is_offline", {
    is: false,
    then: yup.string().required("Due date is required"),
    otherwise: yup.string(),
  }),
  expires_at: yup.string().when("is_offline", {
    is: false,
    then: yup.string().required("Expiry date is required"),
    otherwise: yup.string(),
  }),
  duration: yup
    .number()
    .required("Duration is required")
    .positive("Duration must be positive"),
  instruction: yup.string().required("Instructions are required"),
  accessibility: yup.string().when("is_offline", {
    is: false,
    then: yup.string().required("Accessibility option is required"),
    otherwise: yup.string(),
  }),
  students: yup.array().when(["is_offline", "accessibility"], {
    is: (is_offline, accessibility) =>
      !is_offline && accessibility !== "whole_class",
    then: yup.array().min(1, "Select at least one student"),
    otherwise: yup.array(),
  }),
});

const NewQuiz = () => {
  const [openSuccess, setOpenSuccess] = useState(false);
  const [createQuiz, { isLoading: isCreatingQuiz }] = useCreateQuizMutation();
  const navigate = useNavigate();

  // Determine if the current user is an examiner (not also a teacher)
  const currentUser = useSelector(selectCurrentUser);
  const userRoles =
    currentUser?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isTeacher = userRoles.includes("teacher");
  const isExaminer = userRoles.includes("examiner");
  // Examiner always fetches all classes, regardless of other roles
  const useAllClasses = isExaminer;

  // Track selected classroom and subject for fetching related data
  const [selectedClassroomId, setSelectedClassroomId] = useState(null);
  const [selectedClassroom, setSelectedClassroom] = useState(null);

  // Fetch all teacher subjects (contains class + subject + level info) — skipped for examiner
  const {
    data: teacherSubjectsData = [],
    isLoading: isFetchingTeacherSubjects,
  } = useGetTeacherSubjectsQuery(undefined, { skip: useAllClasses });

  // For examiner: fetch all classrooms and subjects per classroom
  const { data: allClassroomsData, isLoading: isFetchingAllClassrooms } =
    useGetClassesPaginatedQuery(
      { page: 1, per_page: 200 },
      { skip: !useAllClasses },
    );
  const { data: classroomSubjectsData = [] } = useGetClassroomSubjectsQuery(
    { classroom_id: selectedClassroomId },
    { skip: !useAllClasses || !selectedClassroomId },
  );

  const isLoadingClassrooms = useAllClasses
    ? isFetchingAllClassrooms
    : isFetchingTeacherSubjects;

  // Derive unique classrooms from teacher subjects (teacher) or all classrooms (examiner)
  const classrooms = useMemo(() => {
    if (useAllClasses) {
      return allClassroomsData?.classrooms || [];
    }
    if (!teacherSubjectsData) return [];
    const classMap = new Map();
    teacherSubjectsData.forEach((item) => {
      if (!classMap.has(item.classroom_id)) {
        classMap.set(item.classroom_id, {
          id: item.classroom_id,
          name: item.classroom,
          level: item.level,
        });
      }
    });
    return Array.from(classMap.values());
  }, [useAllClasses, teacherSubjectsData, allClassroomsData]);

  // Derive subjects for selected classroom
  const subjects = useMemo(() => {
    if (useAllClasses) {
      return classroomSubjectsData.map((s) => ({ id: s.id, name: s.name }));
    }
    if (!teacherSubjectsData || !selectedClassroomId) return [];
    return teacherSubjectsData
      .filter((item) => item.classroom_id === parseInt(selectedClassroomId))
      .map((item) => ({ id: item.id, name: item.name }));
  }, [
    useAllClasses,
    teacherSubjectsData,
    classroomSubjectsData,
    selectedClassroomId,
  ]);

  // Fetch assessments (quiz types) for selected classroom
  const { data: assessmentsData = {}, isFetching: isFetchingAssessments } =
    useGetClassroomAssessmentsQuery(
      { classroom_id: selectedClassroomId },
      { skip: !selectedClassroomId },
    );

  const assessments = assessmentsData.assessments || [];

  // Fetch students for selected classroom
  const { data: students = [] } = useGetClassroomStudentsQuery(
    { classroom_id: selectedClassroomId },
    { skip: !selectedClassroomId },
  );

  // Form initialization and handling (must come before query hooks that use formik)
  const formik = useFormik({
    initialValues: {
      classroom_id: "",
      subject: "",
      type: "",
      selectedTopics: [],
      topicQuestions: {}, // { topic_name: question_count }
      is_offline: false,
      due_at: "",
      expires_at: "",
      duration: 0,
      instruction: "",
      accessibility: "whole_class",
      students: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      // Format selected topics with question counts
      const topics = values.selectedTopics.map((topicName) => ({
        name: topicName,
        count: parseInt(values.topicQuestions[topicName]) || 0,
      }));

      // Handle accessibility - convert to students list
      let finalStudents = values.students;
      if (values.accessibility === "all_except" && students.length > 0) {
        // Get all student IDs not in the exclusion list
        const allStudentIds = students.map((s) => s.user_id);
        finalStudents = allStudentIds.filter(
          (id) => !values.students.includes(id),
        );
      } else if (values.accessibility === "whole_class") {
        // Server will resolve all students in the class — send empty list
        finalStudents = [];
      }

      const submitData = {
        classroom_id: values.classroom_id,
        accessibility: values.accessibility,
        subject: values.subject,
        level: selectedClassroom?.level,
        type: values.type,
        duration: parseInt(values.duration),
        due_at: values.is_offline ? null : localToUTC(values.due_at),
        expires_at: values.is_offline ? null : localToUTC(values.expires_at),
        instruction: values.instruction,
        topics,
        students: values.is_offline ? [] : finalStudents,
        is_offline: values.is_offline,
      };

      console.log("submitData", submitData);

      try {
        await createQuiz(submitData).unwrap();
        setOpenSuccess(true);
      } catch (error) {
        console.error(error);
      }
    },
  });

  console.log("due at", formik.values.due_at);
  console.log("expires at", formik.values.expires_at);

  const submitRef = useRef(null);
  useEffect(() => {
    submitRef.current = formik.handleSubmit;
  });
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        submitRef.current?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  const { data: topicsData = {}, isFetching: isFetchingTopics } =
    useGetTopicsBySubjectAndLevelQuery(
      {
        subject: formik.values.subject,
        level: selectedClassroom?.level,
      },
      {
        skip: !formik.values.subject || !selectedClassroom?.level,
      },
    );

  const availableTopics = topicsData.topics || [];

  // Handle classroom selection
  const handleClassroomChange = (event) => {
    const classroomId = event.target.value;
    formik.setFieldValue("classroom_id", classroomId);
    setSelectedClassroomId(classroomId);

    const classroom = classrooms.find((c) => c.id === parseInt(classroomId));
    setSelectedClassroom(classroom);

    // Reset dependent fields
    formik.setFieldValue("subject", "");
    formik.setFieldValue("type", "");
    formik.setFieldValue("selectedTopics", []);
    formik.setFieldValue("topicQuestions", {});
  };

  // Handle subject change
  const handleSubjectChange = (event) => {
    formik.setFieldValue("subject", event.target.value);
    formik.setFieldValue("selectedTopics", []);
    formik.setFieldValue("topicQuestions", {});
  };

  // Handle topic selection
  const handleTopicToggle = (topicName) => {
    const isSelected = formik.values.selectedTopics.includes(topicName);
    const newTopics = isSelected
      ? formik.values.selectedTopics.filter((t) => t !== topicName)
      : [...formik.values.selectedTopics, topicName];

    formik.setFieldValue("selectedTopics", newTopics);

    // Initialize question count if not already set
    if (!isSelected && !formik.values.topicQuestions[topicName]) {
      const topicCount =
        availableTopics.find((t) => t.name === topicName)?.count || 0;
      formik.setFieldValue("topicQuestions", {
        ...formik.values.topicQuestions,
        [topicName]: Math.min(topicCount, 5), // Default to 5 or available count
      });
    }
  };

  // Handle question count change for topic
  const handleQuestionCountChange = (topicName, count) => {
    formik.setFieldValue("topicQuestions", {
      ...formik.values.topicQuestions,
      [topicName]: parseInt(count) || 0,
    });
  };

  // Handle accessibility change
  const handleAccessibilityChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue("accessibility", value);
    if (value === "whole_class") {
      // Auto-populate all students in the class as participants
      formik.setFieldValue(
        "students",
        students.map((s) => s.user_id),
      );
    } else {
      formik.setFieldValue("students", []);
    }
  };

  // Calculate total questions selected
  const totalQuestionsSelected = formik.values.selectedTopics.reduce(
    (sum, topic) => sum + (parseInt(formik.values.topicQuestions[topic]) || 0),
    0,
  );

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 2 }}>
      {/* Success Dialog */}
      <Dialog
        fullWidth={true}
        open={openSuccess}
        onClose={() => setOpenSuccess(false)}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "24px",
            maxWidth: "776px",
          },
        }}
      >
        <Stack sx={{ alignItems: "center", gap: 2.5, py: 5, px: 3 }}>
          <TaskAlt
            sx={{ width: "136px", height: "162px", color: "primary.main" }}
          />
          <Typography
            sx={{ color: "text.primary", fontSize: "20px", fontWeight: 600 }}
          >
            Success
          </Typography>
          <Typography
            sx={{ color: "#95969D", fontSize: "16px", textAlign: "center" }}
          >
            Your quiz has been scheduled successfully
          </Typography>
          <FilledButton
            onClick={() => {
              setOpenSuccess(false);
              navigate(-1);
            }}
            sx={{ width: "240px", fontWeight: 400, fontSize: "14px" }}
          >
            BACK TO HOME
          </FilledButton>
        </Stack>
      </Dialog>

      {/* Page Header */}
      <Typography
        sx={{
          fontSize: { xs: "1rem", sm: "1.25rem" },
          fontWeight: 600,
          mb: { xs: 2, md: 3 },
        }}
      >
        Create New Quiz
      </Typography>

      {/* Quiz Mode Toggle */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: "text.secondary" }}
        >
          Quiz Mode:
        </Typography>
        <ToggleButtonGroup
          value={formik.values.is_offline ? "offline" : "online"}
          exclusive
          onChange={(_, val) => {
            if (val !== null)
              formik.setFieldValue("is_offline", val === "offline");
          }}
          size="small"
        >
          <ToggleButton
            value="online"
            sx={{ gap: 0.75, textTransform: "none", px: 2 }}
          >
            <Wifi fontSize="small" />
            Online
          </ToggleButton>
          <ToggleButton
            value="offline"
            sx={{ gap: 0.75, textTransform: "none", px: 2 }}
          >
            <WifiOff fontSize="small" />
            Offline
          </ToggleButton>
        </ToggleButtonGroup>
        {formik.values.is_offline && (
          <Typography variant="caption" color="text.secondary">
            Students won't be assigned — quiz is for printing or downloading
            only.
          </Typography>
        )}
      </Box>

      {/* Step 1: Class, Subject & Quiz Type */}
      <Card
        sx={{
          mb: 4,
          border: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              1
            </Box>
            Class, Subject & Quiz Type
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  name="classroom_id"
                  value={formik.values.classroom_id}
                  onChange={handleClassroomChange}
                  label="Class"
                  disabled={isLoadingClassrooms}
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Select a class</em>
                  </MenuItem>
                  {classrooms.map((classroom) => (
                    <MenuItem value={classroom.id} key={classroom.id}>
                      {classroom.name}
                      {classroom.level && (
                        <Chip
                          label={classroom.level}
                          size="small"
                          sx={{ ml: 2 }}
                          variant="outlined"
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.classroom_id && formik.errors.classroom_id && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.classroom_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  name="subject"
                  value={formik.values.subject}
                  onChange={handleSubjectChange}
                  label="Subject"
                  disabled={!selectedClassroom}
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Select a subject</em>
                  </MenuItem>
                  {subjects.map((subject) => (
                    <MenuItem value={subject.name} key={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.subject && formik.errors.subject && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.subject}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Quiz Type</InputLabel>
                <Select
                  name="type"
                  value={formik.values.type}
                  onChange={(e) => formik.setFieldValue("type", e.target.value)}
                  label="Quiz Type"
                  disabled={!selectedClassroom || isFetchingAssessments}
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Select a quiz type</em>
                  </MenuItem>
                  {assessments.map((assessment) => (
                    <MenuItem value={assessment.name} key={assessment.id}>
                      {assessment.name}
                      {assessment.is_exam && (
                        <Chip
                          label="Exam"
                          size="small"
                          variant="outlined"
                          sx={{ ml: 2 }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.type && formik.errors.type && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.type}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedClassroom && (
        <>
          {/* Step 2: Select Topics */}
          {formik.values.subject && (
            <Card
              sx={{
                mb: 4,
                border: (theme) =>
                  `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    2
                  </Box>
                  Select Topics & Questions
                </Typography>

                {isFetchingTopics ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 3 }}
                  >
                    <CircularProgress size={32} />
                  </Stack>
                ) : availableTopics.length === 0 ? (
                  <Alert severity="info">
                    No topics available for the selected subject and class level
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ display: "flex", gap: 0.5 }}
                    >
                      <Info sx={{ fontSize: "1rem" }} />
                      Select at least one topic. Adjust the number of questions
                      you want from each.
                    </Typography>
                    {availableTopics.map((topic) => (
                      <Paper
                        key={topic.name}
                        sx={{
                          p: 2,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#2a2a2a"
                              : "#F9F9F9",
                          border: (theme) =>
                            `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formik.values.selectedTopics.includes(
                                  topic.name,
                                )}
                                onChange={() => handleTopicToggle(topic.name)}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {topic.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {topic.count} questions available
                                </Typography>
                              </Box>
                            }
                          />
                          {formik.values.selectedTopics.includes(
                            topic.name,
                          ) && (
                            <TextField
                              type="number"
                              value={
                                formik.values.topicQuestions[topic.name] || 0
                              }
                              onChange={(e) =>
                                handleQuestionCountChange(
                                  topic.name,
                                  e.target.value,
                                )
                              }
                              inputProps={{ min: 0, max: topic.count }}
                              size="small"
                              label="Questions"
                              sx={{ width: "100px" }}
                            />
                          )}
                        </Stack>
                      </Paper>
                    ))}
                    {formik.touched.selectedTopics &&
                      formik.errors.selectedTopics && (
                        <Typography color="error" variant="caption">
                          {formik.errors.selectedTopics}
                        </Typography>
                      )}
                    <Divider />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Total Questions Selected:{" "}
                      <strong>{totalQuestionsSelected}</strong>
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}
          {/* Step 3: Quiz Settings */}
          <Card
            sx={{
              mb: 4,
              border: (theme) =>
                `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  3
                </Box>
                Quiz Settings
              </Typography>

              <Grid container spacing={3}>
                {!formik.values.is_offline && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth>
                        <TextField
                          label="Due Date"
                          name="due_at"
                          type="datetime-local"
                          value={formik.values.due_at}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "#2a2a2a"
                                  : "#F9F9F9",
                              borderRadius: "12px",
                            },
                          }}
                          error={
                            formik.touched.due_at &&
                            Boolean(formik.errors.due_at)
                          }
                          helperText={
                            formik.touched.due_at && formik.errors.due_at
                          }
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth>
                        <TextField
                          label="Expiry Date"
                          name="expires_at"
                          type="datetime-local"
                          value={formik.values.expires_at}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "#2a2a2a"
                                  : "#F9F9F9",
                              borderRadius: "12px",
                            },
                          }}
                          error={
                            formik.touched.expires_at &&
                            Boolean(formik.errors.expires_at)
                          }
                          helperText={
                            formik.touched.expires_at &&
                            formik.errors.expires_at
                          }
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <TextField
                      label="Duration (minutes)"
                      name="duration"
                      type="number"
                      value={formik.values.duration}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputProps={{ inputProps: { min: 1 } }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#2a2a2a"
                              : "#F9F9F9",
                          borderRadius: "12px",
                        },
                      }}
                      error={
                        formik.touched.duration &&
                        Boolean(formik.errors.duration)
                      }
                      helperText={
                        formik.touched.duration && formik.errors.duration
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {/* Step 4: Instructions */}
          <Card
            sx={{
              mb: 4,
              border: (theme) =>
                `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  4
                </Box>
                Instructions
              </Typography>
              <CustomTipTapEditor
                value={formik.values.instruction}
                onChange={(html) => formik.setFieldValue("instruction", html)}
                placeholder="Enter quiz instructions here..."
                showToolbar={true}
                minHeight="200px"
              />
              {formik.touched.instruction && formik.errors.instruction && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, display: "block" }}
                >
                  {formik.errors.instruction}
                </Typography>
              )}
            </CardContent>
          </Card>
          {/* Step 5: Accessibility (online quizzes only) */}
          {!formik.values.is_offline && (
            <Card
              sx={{
                mb: 4,
                border: (theme) =>
                  `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    5
                  </Box>
                  Accessibility
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Who can see this quiz?</InputLabel>
                      <Select
                        name="accessibility"
                        value={formik.values.accessibility}
                        onChange={handleAccessibilityChange}
                        label="Who can see this quiz?"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#2a2a2a"
                              : "#F9F9F9",
                          borderRadius: "12px",
                        }}
                      >
                        <MenuItem value="whole_class">Whole Class</MenuItem>
                        <MenuItem value="selected_students">
                          Selected Students Only
                        </MenuItem>
                        <MenuItem value="all_except">
                          All Students Except Selected
                        </MenuItem>
                      </Select>
                      {formik.touched.accessibility &&
                        formik.errors.accessibility && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ mt: 1 }}
                          >
                            {formik.errors.accessibility}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>

                  {formik.values.accessibility !== "whole_class" && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {formik.values.accessibility === "selected_students"
                            ? "Select Students to Include"
                            : "Select Students to Exclude"}
                        </InputLabel>
                        <Select
                          multiple
                          name="students"
                          value={formik.values.students}
                          onChange={formik.handleChange}
                          label={
                            formik.values.accessibility === "selected_students"
                              ? "Select Students to Include"
                              : "Select Students to Exclude"
                          }
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "#2a2a2a"
                                : "#F9F9F9",
                            borderRadius: "12px",
                          }}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((studentId) => {
                                const student = students.find(
                                  (s) => s.user_id === studentId,
                                );
                                return (
                                  <Chip
                                    key={studentId}
                                    label={`${student?.first_name} ${student?.last_name}`}
                                    size="small"
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {students.map((student) => (
                            <MenuItem value={student.user_id} key={student.id}>
                              <Checkbox
                                checked={formik.values.students.includes(
                                  student.user_id,
                                )}
                              />
                              {student.first_name} {student.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.students && formik.errors.students && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ mt: 1 }}
                          >
                            {formik.errors.students}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}{" "}
          {/* end !is_offline accessibility */}
          {/* Submit Button */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 6, mb: 4 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ minWidth: 150 }}
            >
              Cancel
            </Button>
            <FilledButton
              type="submit"
              disabled={isCreatingQuiz || !formik.isValid}
              sx={{
                minWidth: 200,
                fontWeight: 500,
                fontSize: "16px",
              }}
            >
              {isCreatingQuiz ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Creating...
                </>
              ) : (
                <>
                  <Check sx={{ mr: 1 }} /> Create Quiz
                </>
              )}
            </FilledButton>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default NewQuiz;
